import React, { useState } from 'react';

import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faBell } from '@fortawesome/pro-regular-svg-icons/faBell';
import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import { faClock } from '@fortawesome/pro-regular-svg-icons/faClock';
import { faTimes } from '@fortawesome/pro-regular-svg-icons/faTimes';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons/faExclamationTriangle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { TaskState } from '../constants/states';
import iconstyles from './iconstyles.module.scss';

/**
 * A Nora specific component that overrides the ethos-design-system Icon component
 * with the correct font awesome prefix and icon name based on Nora task type.
 *
 * @public
 *
 * @param {string} type - task state type e.g. 'ordered'
 *
 * @return {JSX.Element}
 */

export const TaskIcon = ({
  id,
  state,
  onClick,
  disabled,
}: {
  id: number;
  state: string;
  onClick?: any;
  disabled?: boolean;
}) => {
  const stylesMap: {
    [key: string]: any;
  } = {
    ORDERED: iconstyles.Ordered,
    WAIVED: iconstyles.Waived,
    FOR_REVIEW: iconstyles.Review,
    COMPLETED: iconstyles.Completed,
    ERROR: iconstyles.Error,
    REQUESTED: iconstyles.Requested,
  };

  const iconName = (taskState: string): IconDefinition => {
    switch (taskState) {
      case 'ORDERED':
        return faClock;
      case 'WAIVED':
        return faTimes;
      case 'FOR_REVIEW':
      case 'COMPLETED':
        return faCheck;
      case 'ERROR':
        return faExclamationTriangle;
      case 'REQUESTED':
        return faBell;
      default:
        return faClock;
    }
  };

  const [canComplete] = useState(state === TaskState.FOR_REVIEW);
  const [canWaive] = useState(
    state === TaskState.ORDERED || state === TaskState.ERROR,
  );
  const canOrder = useState(state === TaskState.REQUESTED);

  const [hovered, setHovered] = useState(false);

  let iconContainerClasses = [iconstyles.TaskIcon, stylesMap[state]].join(' ');
  if (hovered && canWaive) {
    iconContainerClasses = `${iconContainerClasses} ${iconstyles.WaivableHovered}`;
  } else if (hovered && canComplete) {
    iconContainerClasses = `${iconContainerClasses} ${iconstyles.CompletableHovered}`;
  } else if (hovered && canOrder) {
    iconContainerClasses = `${iconContainerClasses} ${iconstyles.OrderHovered}`;
  }

  const getIconName = (): IconDefinition => {
    if (hovered && canWaive) {
      return iconName(TaskState.WAIVED);
    }

    if (hovered && canComplete) {
      return iconName(TaskState.COMPLETED);
    }

    if (hovered && canOrder) {
      return iconName(TaskState.ORDERED);
    }

    return iconName(state);
  };

  return (
    <button
      onClick={() => !!onClick && onClick(id, state)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={iconContainerClasses}
      disabled={disabled}
    >
      <FontAwesomeIcon icon={getIconName()}></FontAwesomeIcon>
    </button>
  );
};
